import { createApi } from "@reduxjs/toolkit/query/react";
import { ApiEndpoints, baseQueryWithAuth } from "../../core";
import { toast } from "../../core/toast";
import {
  PaymentSubmitRequest,
  PaymentSubmitResponse,
} from "../../types/subscription/subscriptionPayment";

export const subscriptionPaymentApi = createApi({
  reducerPath: "subscriptionPaymentApi",
  baseQuery: baseQueryWithAuth,
  tagTypes: ["SubscriptionPayment", "SubscriptionPayments"],

  endpoints: (builder) => ({
    createSubscriptionPayment: builder.mutation<
      PaymentSubmitResponse,
      PaymentSubmitRequest
    >({
      query: (payload) => ({
        url: ApiEndpoints.createSubscriptionPayment,
        method: "POST",
        body: payload,
      }),
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          toast.success(data?.message || "Payment submitted successfully");
        } catch (err: any) {
          toast.error(err?.error?.data?.message || "Error submitting payment");
        }
      },
      invalidatesTags: ["SubscriptionPayment"],
    }),
    getPaymentDetails: builder.query<PaymentSubmitResponse, { organisationId: string }>({
      query: ({ organisationId }) => ({
        url: `${ApiEndpoints.getSubscriptionPayment}?organisation=${organisationId}`,
        method: "GET",
      }),
      providesTags: ["SubscriptionPayments"],
    }),
    getPaymentMethodById: builder.query<any, string>({
      query: (id) => ({
        url: `${ApiEndpoints.getSubscriptionPayment}/${id}`,
        method: "GET",
      }),
      providesTags: (result, error, id) => [{ type: "SubscriptionPayment", id }],
    }),
    updateSubscriptionPayment: builder.mutation<
      PaymentSubmitResponse,
      { id: string; payload: any }
    >({
      query: ({ id, payload }) => ({
        url: `${ApiEndpoints.getSubscriptionPayment}/${id}`,
        method: "PATCH",
        body: payload,
      }),
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          toast.success(data?.message || "Payment updated successfully");
        } catch (err: any) {
          toast.error(err?.error?.data?.message || "Error updating payment");
        }
      },
      invalidatesTags: (result, error, { id }) => [
        { type: "SubscriptionPayment", id },
        "SubscriptionPayments",
      ],
    }),
    deleteSubscriptionPayment: builder.mutation<PaymentSubmitResponse, string>({
      query: (id) => ({
        url: `${ApiEndpoints.getSubscriptionPayment}/${id}`,
        method: "DELETE",
      }),
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          toast.success(data?.message || "Payment deleted successfully");
        } catch (err: any) {
          toast.error(err?.error?.data?.message || "Error deleting payment");
        }
      },
      invalidatesTags: ["SubscriptionPayments"],
    }),
    setDefaultPaymentMethod: builder.mutation<PaymentSubmitResponse, string>({
      query: (id) => ({
        url: `${ApiEndpoints.getSubscriptionPayment}/change-default/${id}`,
        method: "PATCH",
      }),
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          toast.success(data?.message || "Default payment method updated");
        } catch (err: any) {
          toast.error(err?.error?.data?.message || "Error updating default payment");
        }
      },
      invalidatesTags: ["SubscriptionPayments"],
    }),
  }),
});

export const {
  useCreateSubscriptionPaymentMutation,
  useGetPaymentDetailsQuery,
  useGetPaymentMethodByIdQuery,
  useUpdateSubscriptionPaymentMutation,
  useDeleteSubscriptionPaymentMutation,
  useSetDefaultPaymentMethodMutation,
} = subscriptionPaymentApi;
